import React from 'react'
import { Editor, type BeforeMount, type OnMount } from '@monaco-editor/react'
import type { editor as monacoEditor } from 'monaco-editor'
import { Maximize2, Minimize2, Braces, FileText, Copy, Check, Minimize, Zap, Database } from 'lucide-react'
import { format as formatSqlQuery } from 'sql-formatter'
import styles from './TemplateEditor.module.css'
import { safeJsonParse, safeJsonStringify, sanitizeJsonInput, MAX_LENGTHS } from '../../utils/security'
import { toast } from '../../lib/toast'
import { createPortal } from 'react-dom'

interface MakeDynamicInfo {
  variableName: string
  originalValue: string
  targetPath: string
}

/** A `{{ variable }}` token in the editor, with 1-based Monaco columns (endColumn is exclusive). */
interface DynamicToken {
  lineNumber: number
  startColumn: number
  endColumn: number
  name: string
}

/** Lowercase the name and keep only characters that are legal in a Jinja variable. */
const sanitizeVariableName = (raw: string): string =>
  raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')

/**
 * Find the `{{ token }}` that fully contains the caret/selection.
 * A double-click inside `{{ userId }}` only selects `userId`, so we resolve that back to the
 * whole token - otherwise the editor would offer to wrap it again and nest the braces.
 */
const findEnclosingToken = (
  model: monacoEditor.ITextModel,
  selection: { startLineNumber: number; startColumn: number; endLineNumber: number; endColumn: number }
): DynamicToken | null => {
  if (selection.startLineNumber !== selection.endLineNumber) return null
  const lineText = model.getLineContent(selection.startLineNumber)
  const tokenRe = /\{\{\s*([^{}]*?)\s*\}\}/g
  let match: RegExpExecArray | null
  while ((match = tokenRe.exec(lineText)) !== null) {
    const startColumn = match.index + 1
    const endColumn = startColumn + match[0].length
    if (selection.startColumn >= startColumn && selection.endColumn <= endColumn) {
      // `{{ name | filter }}` - the variable is the part before the filter
      const name = match[1].split('|')[0].trim()
      return { lineNumber: selection.startLineNumber, startColumn, endColumn, name }
    }
  }
  return null
}

/**
 * Find the quoted JSON string *value* that contains the caret/selection.
 * Property keys (a string followed by `:`) are skipped so we never swallow the key.
 */
const findEnclosingStringValue = (
  model: monacoEditor.ITextModel,
  selection: { startLineNumber: number; startColumn: number; endLineNumber: number; endColumn: number }
): { startColumn: number; endColumn: number; text: string; inner: string } | null => {
  if (selection.startLineNumber !== selection.endLineNumber) return null
  const lineText = model.getLineContent(selection.startLineNumber)
  const stringRe = /"(?:[^"\\]|\\.)*"/g
  let match: RegExpExecArray | null
  while ((match = stringRe.exec(lineText)) !== null) {
    const startColumn = match.index + 1
    const endColumn = startColumn + match[0].length
    if (selection.startColumn < startColumn || selection.endColumn > endColumn) continue
    const isKey = /^\s*:/.test(lineText.slice(match.index + match[0].length))
    if (isKey) return null
    return { startColumn, endColumn, text: match[0], inner: match[0].slice(1, -1) }
  }
  return null
}

interface TemplateEditorProps {
  value: string
  onChange: (value: string) => void
  onMakeDynamic?: (info: MakeDynamicInfo) => void
  onMakeUndynamic?: (newTemplate: string) => void
  getOriginalValue?: (variableName: string) => string | undefined
  height?: string
  letDynamic?: boolean
  readOnly?: boolean
  title?: string
  placeholder?: string
  templateType?: 'request' | 'response'
  undynamicFallback?: 'empty' | 'fieldName'
  contentType?: 'json' | 'sql'
}

export default function TemplateEditor({
  value,
  onChange,
  height = '300px',
  readOnly = false,
  title = 'Template Editor',
  placeholder = '',
  letDynamic = true,
  onMakeDynamic,
  onMakeUndynamic,
  getOriginalValue,
  undynamicFallback = 'empty',
  contentType = 'json',
}: TemplateEditorProps) {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [tempValue, setTempValue] = React.useState(value)
  const [copied, setCopied] = React.useState(false)
  // `token` is set when the caret/selection sits inside a {{ variable }} (offer "Make Static"),
  // `canWrap` when a plain, brace-free selection can be wrapped (offer "Make Dynamic").
  const [selectionState, setSelectionState] = React.useState<{ token: DynamicToken | null; canWrap: boolean }>({
    token: null,
    canWrap: false,
  })
  const editorRef = React.useRef<monacoEditor.IStandaloneCodeEditor | null>(null)

  React.useEffect(() => {
    setTempValue(value)
  }, [value])

  const getCurrentValue = () => isExpanded ? tempValue : value

  const setCurrentValue = (val: string) => {
    if (isExpanded) {
      setTempValue(val)
    } else {
      onChange(val)
    }
  }

  React.useEffect(() => {
  if (!isExpanded) return
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') handleCancel()
  }
  document.addEventListener('keydown', onKeyDown)
  const prevOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  return () => {
    document.removeEventListener('keydown', onKeyDown)
    document.body.style.overflow = prevOverflow
  }
}, [isExpanded])

  const handleEditorChange = (newValue: string | undefined) => {
    const val = newValue || ''
    const sanitized = sanitizeJsonInput(val)
    if (sanitized.length > MAX_LENGTHS.json) {
      return
    }
    if (isExpanded) {
      setTempValue(sanitized)
    } else {
      onChange(sanitized)
    }
  }

  const prepareForParse = (input: string) => {
    const placeholders: Record<string, string> = {}
    let idx = 0
    const replaced = input.replace(/"?\{\{([^}]*)\}\}"?/g, (match, inner) => {
      const isQuoted = match.startsWith('"') && match.endsWith('"')
      const key = `__TPL_${idx++}__`
      placeholders[`"${key}"`] = isQuoted ? `"{{${inner}}}"` : `{{${inner}}}`
      return isQuoted ? `"${key}"` : `"${key}"`
    })
    return { replaced, placeholders }
  }

  const restorePlaceholders = (output: string, placeholders: Record<string, string>) => {
    let result = output
    for (const [key, original] of Object.entries(placeholders)) {
      result = result.replace(key, original)
    }
    return result
  }

  const formatJson = () => {
    const current = getCurrentValue()
    if (!current.trim()) return
    const { replaced, placeholders } = prepareForParse(current)
    const result = safeJsonParse(replaced, {})
    if (result.success) {
      const formatted = restorePlaceholders(safeJsonStringify(result.data, '{}'), placeholders)
      setCurrentValue(formatted)
      toast.success('JSON formatted')
    } else {
      toast.error(`Invalid JSON: ${result.error}`)
    }
  }

  const minifyJson = () => {
    const current = getCurrentValue()
    if (!current.trim()) return
    const { replaced, placeholders } = prepareForParse(current)
    const result = safeJsonParse(replaced, {})
    if (result.success) {
      try {
        const minified = restorePlaceholders(JSON.stringify(result.data), placeholders)
        setCurrentValue(minified)
        toast.success('JSON minified')
      } catch {
        toast.error('Failed to minify JSON')
      }
    } else {
      toast.error(`Invalid JSON: ${result.error}`)
    }
  }

  const formatSql = () => {
    const current = getCurrentValue()
    if (!current.trim()) return
    // {{ paramName }} isn't valid T-SQL syntax, so swap it for a plain identifier
    // before formatting and restore it afterward to keep the placeholder intact.
    const placeholders: Record<string, string> = {}
    let idx = 0
    const replaced = current.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, name) => {
      const key = `__TPL_${idx++}__`
      placeholders[key] = `{{ ${name} }}`
      return key
    })
    try {
      let formatted = formatSqlQuery(replaced, { language: 'tsql', tabWidth: 2, keywordCase: 'upper' })
      for (const [key, original] of Object.entries(placeholders)) {
        formatted = formatted.split(key).join(original)
      }
      setCurrentValue(formatted)
      toast.success('SQL formatted')
    } catch (error) {
      toast.error(`Failed to format SQL: ${error instanceof Error ? error.message : 'unknown error'}`)
    }
  }

  const copyToClipboard = async () => {
    const current = getCurrentValue()
    if (!current.trim()) return
    try {
      await navigator.clipboard.writeText(current)
      setCopied(true)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy to clipboard')
    }
  }

  const handleExpand = () => {
    setTempValue(value)
    setIsExpanded(true)
  }

  const handleCollapse = () => {
    onChange(tempValue)
    setIsExpanded(false)
  }

  const handleCancel = () => {
    setTempValue(value)
    setIsExpanded(false)
  }

  const handleBeforeMount: BeforeMount = (monaco) => {
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: false,
      schemaValidation: 'ignore',
      enableSchemaRequest: false,
      allowComments: true,
      trailingCommas: 'ignore',
    })
  }

  const handleEditorMount: OnMount = (editor) => {
    editorRef.current = editor
    // The expanded modal mounts a second editor - keep the ref on whichever one is in use
    editor.onDidFocusEditorText(() => {
      editorRef.current = editor
    })
    editor.onDidChangeCursorSelection(() => {
      const selection = editor.getSelection()
      const model = editor.getModel()
      if (!model || !selection) {
        setSelectionState({ token: null, canWrap: false })
        return
      }
      const token = findEnclosingToken(model, selection)
      if (token) {
        setSelectionState({ token, canWrap: false })
        return
      }
      const selectedText = selection.isEmpty() ? '' : model.getValueInRange(selection)
      const canWrap = selectedText.trim().length > 0 && !/\{\{|\}\}/.test(selectedText)
      setSelectionState({ token: null, canWrap })
    })
  }

  /**
   * Name the variable after the JSON property it belongs to (lowercased), so
   * `"customerName": "John Doe"` becomes `"customerName": "{{ customername }}"`.
   * Falls back to the selected text, and only then to a generic placeholder.
   */
  const getVariableNameForSelection = (selectedText: string): string => {
    const editor = editorRef.current
    if (!editor) return sanitizeVariableName(selectedText) || 'field_name'
    const selection = editor.getSelection()
    const model = editor.getModel()
    if (!model || !selection) return sanitizeVariableName(selectedText) || 'field_name'

    const fullText = model.getValue()
    const startOffset = model.getOffsetAt({
      lineNumber: selection.startLineNumber,
      column: selection.startColumn,
    })
    const endOffset = model.getOffsetAt({
      lineNumber: selection.endLineNumber,
      column: selection.endColumn,
    })

    // The selection is the property key itself - keep that name.
    if (/^"?\s*:/.test(fullText.slice(endOffset, endOffset + 20))) {
      return sanitizeVariableName(selectedText) || 'field_name'
    }

    // Otherwise use the nearest property key before the selection.
    const keyMatches = [...fullText.slice(0, startOffset).matchAll(/"([^"\\]+)"\s*:/g)]
    const nearestKey = keyMatches.length ? keyMatches[keyMatches.length - 1][1] : ''
    return sanitizeVariableName(nearestKey) || sanitizeVariableName(selectedText) || 'field_name'
  }

  const handleMakeDynamic = () => {
    const editor = editorRef.current
    if (!editor) return
    const selection = editor.getSelection()
    const model = editor.getModel()
    if (!model || !selection || selection.isEmpty()) return

    const selectedText = model.getValueInRange(selection)
    if (findEnclosingToken(model, selection) || /\{\{|\}\}/.test(selectedText)) return

    // A double-click only selects one word, so grow the edit to the whole JSON string value
    // when the selection sits inside one - "John Doe" must not become "{{ name }} Doe".
    const stringValue = findEnclosingStringValue(model, selection)
    const sanitized = getVariableNameForSelection(stringValue ? stringValue.inner : selectedText)

    const startCol = selection.startColumn
    const endCol = selection.endColumn
    const lineText = model.getLineContent(selection.startLineNumber)
    const charBefore = startCol > 1 ? lineText[startCol - 2] : ''
    const charAfter = endCol <= lineText.length ? lineText[endCol - 1] : ''
    // Selection already covers the whole string content, with the quotes just outside it
    const isQuotedValue = charBefore === '"' && charAfter === '"' && !selectedText.startsWith('"')

    const replaceText = `{{ ${sanitized} }}`

    let rangeStartCol = startCol
    let rangeEndCol = endCol
    let originalValue = selectedText
    if (stringValue) {
      // Replace the quoted string as a whole; the token stands in for the quotes too
      rangeStartCol = stringValue.startColumn
      rangeEndCol = stringValue.endColumn
      originalValue = stringValue.text
    } else if (isQuotedValue) {
      rangeStartCol = startCol - 1
      rangeEndCol = endCol + 1
      originalValue = `"${selectedText}"`
    }

    editor.executeEdits('make-dynamic', [{
      range: {
        startLineNumber: selection.startLineNumber,
        startColumn: rangeStartCol,
        endLineNumber: stringValue ? selection.startLineNumber : selection.endLineNumber,
        endColumn: rangeEndCol,
      },
      text: replaceText,
    }])

    // Keep the new token selected so the button flips straight to "Make Static"
    if (stringValue || selection.startLineNumber === selection.endLineNumber) {
      editor.setSelection({
        startLineNumber: selection.startLineNumber,
        startColumn: rangeStartCol,
        endLineNumber: selection.startLineNumber,
        endColumn: rangeStartCol + replaceText.length,
      })
    }

    // Trigger onChange so the form state updates
    const newValue = model.getValue()
    if (isExpanded) {
      setTempValue(newValue)
    } else {
      onChange(newValue)
    }

    // Notify parent about the original value for restore on removal
    onMakeDynamic?.({
      variableName: sanitized,
      originalValue,
      targetPath: sanitized,
    })
  }

  const handleMakeUndynamic = () => {
    const editor = editorRef.current
    if (!editor) return
    const selection = editor.getSelection()
    const model = editor.getModel()
    if (!model || !selection) return

    const token = findEnclosingToken(model, selection)
    if (!token) return

    const variableName = token.name.trim()
    const originalValue = getOriginalValue?.(variableName)

    // Check if {{ var }} is surrounded by quotes: "{{ var }}"
    const startCol = token.startColumn
    const endCol = token.endColumn
    const lineText = model.getLineContent(token.lineNumber)
    const charBefore = startCol > 1 ? lineText[startCol - 2] : ''
    const charAfter = endCol <= lineText.length ? lineText[endCol - 1] : ''
    const isInsideQuotes = charBefore === '"' && charAfter === '"'

    let replaceText: string
    let rangeStartCol = startCol
    let rangeEndCol = endCol

    if (originalValue !== undefined) {
      if (isInsideQuotes) {
        // Expand range to include surrounding quotes, originalValue already has its own quotes
        rangeStartCol = startCol - 1
        rangeEndCol = endCol + 1
        replaceText = originalValue
      } else {
        replaceText = originalValue
      }
    } else if (undynamicFallback === 'fieldName') {
      // No stored original, but this context has no "leaking a secret" concern
      // (e.g. a SQL column/parameter reference) - restore the bare variable name.
      replaceText = isInsideQuotes ? `"${variableName}"` : variableName
      if (isInsideQuotes) {
        rangeStartCol = startCol - 1
        rangeEndCol = endCol + 1
      }
    } else {
      // No stored original (e.g. the token was already in the saved template): just drop the
      // token and leave the cursor there. We never invent a value - not the field name, and not
      // a pair of quotes the template didn't have: `"CUSTNAME": {{CUSTNAME}}` becomes
      // `"CUSTNAME": `, while `"CUSTNAME": "{{CUSTNAME}}"` keeps its own quotes as `"CUSTNAME": ""`.
      replaceText = ''
    }

    editor.executeEdits('make-undynamic', [{
      range: {
        startLineNumber: token.lineNumber,
        startColumn: rangeStartCol,
        endLineNumber: token.lineNumber,
        endColumn: rangeEndCol,
      },
      text: replaceText,
    }])

    // Select what was restored so the button flips straight back to "Make Dynamic"
    editor.setSelection({
      startLineNumber: token.lineNumber,
      startColumn: rangeStartCol,
      endLineNumber: token.lineNumber,
      endColumn: rangeStartCol + replaceText.length,
    })

    const newValue = model.getValue()
    if (isExpanded) {
      setTempValue(newValue)
    } else {
      // Use dedicated callback to avoid full sync/reorder
      if (onMakeUndynamic) {
        onMakeUndynamic(newValue)
      } else {
        onChange(newValue)
      }
    }
  }

  const editorOptions = {
    readOnly,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    automaticLayout: true,
    tabSize: 2,
    wordWrap: 'on' as const,
    lineNumbers: 'on' as const,
    folding: true,
    bracketPairColorization: { enabled: true },
    autoIndent: 'full' as const,
    formatOnPaste: false,
    overviewRulerLanes: 0,
    renderValidationDecorations: 'off' as const,
  }

  const actionButtons = (
    <div className={styles.actionButtons}>
      {!readOnly && contentType === 'json' && (
        <>
          <button
            type="button"
            onClick={formatJson}
            className={styles.actionBtn}
            title="Format JSON (prettify)"
          >
            <Braces size={13} />
            Format
          </button>
          <button
            type="button"
            onClick={minifyJson}
            className={styles.actionBtn}
            title="Minify JSON"
          >
            <Minimize size={13} />
            Minify
          </button>
        </>
      )}
      {!readOnly && contentType === 'sql' && (
        <button
          type="button"
          onClick={formatSql}
          className={styles.actionBtn}
          title="Format SQL (prettify)"
        >
          <Braces size={13} />
          Format
        </button>
      )}
      <button
        type="button"
        onClick={copyToClipboard}
        className={`${styles.actionBtn} ${copied ? styles.actionBtnSuccess : ''}`}
        title="Copy to clipboard"
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      {!readOnly && selectionState.token && (
        <button
          type="button"
          onClick={handleMakeUndynamic}
          className={`${styles.actionBtn} ${styles.makeUndynamicBtn}`}
          title="Remove {{ }} and restore the original static value"
        >
          <Zap size={13} />
          Make Static
        </button>
      )}
      {!readOnly && selectionState.canWrap && letDynamic && (
        <button
          type="button"
          onClick={handleMakeDynamic}
          className={`${styles.actionBtn} ${styles.makeDynamicBtn}`}
          title="Replace selected value with a dynamic {{ variable }}"
        >
          <Zap size={13} />
          Make Dynamic
        </button>
      )}
    </div>
  )

  return (
    <>
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            {actionButtons}
          </div>
          <div className={styles.toolbarRight}>
            <span className={styles.editorType}>
              {contentType === 'sql' ? <Database size={14} /> : <Braces size={14} />}
              {contentType === 'sql' ? 'SQL' : 'JSON'}
            </span>
            <button
              type="button"
              onClick={handleExpand}
              className={styles.expandBtn}
              title="Expand editor"
            >
              <Maximize2 size={14} />
            </button>
          </div>
        </div>

        <div className={styles.editorWrapper}>
          <Editor
            height={height}
            language={contentType === 'sql' ? 'sql' : 'json'}
            value={isExpanded ? tempValue : value}
            onChange={handleEditorChange}
            onMount={handleEditorMount}
            beforeMount={handleBeforeMount}
            options={editorOptions}
            theme="vs"
          />
          {!value && placeholder && (
            <div className={styles.placeholder}>
              <FileText size={24} />
              <p>{placeholder}</p>
            </div>
          )}
        </div>
      </div>

      {isExpanded && createPortal(
        <div className={styles.modal}>
          <div className={styles.modalOverlay} onClick={handleCancel} />
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>
                <Braces size={18} />
                {title}
              </h3>
              <div className={styles.modalActions}>
                {actionButtons}
                <div className={styles.modalControls}>
                  <button
                    type="button"
                    onClick={handleCollapse}
                    className={styles.saveBtn}
                  >
                    Save & Close
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className={styles.cancelBtn}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCollapse}
                    className={styles.collapseBtn}
                    title="Collapse editor"
                  >
                    <Minimize2 size={16} />
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.modalEditor}>
              <Editor
                height="calc(100vh - 120px)"
                language={contentType === 'sql' ? 'sql' : 'json'}
                value={tempValue}
                onChange={handleEditorChange}
                onMount={handleEditorMount}
                beforeMount={handleBeforeMount}
                options={{
                  ...editorOptions,
                  minimap: { enabled: true },
                  fontSize: 14,
                }}
                theme="vs"
              />
            </div>
          </div>
        </div>,
          document.body
      )}
    </>
  )
}
